import {
  ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy,
  OnInit, Output, ViewChild
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Duration } from 'moment';
import { ModalDirective } from 'ng-uikit-pro-standard';
import { VideoModel } from './video-model';
import { VideoRecordingService } from './video-recording.service';

@Component({
  selector: 'app-video-recorder',
  templateUrl: './video-recorder.component.html',
  styleUrls: ['./video-recorder.component.scss']
})
export class VideoRecorderComponent implements OnInit, OnDestroy {
  @Input() sectionText: string;
  @Output() videoCompleted = new EventEmitter<VideoModel>();

  @ViewChild('videoRecorderModal', { static: true }) videoRecorderModal: ModalDirective;
  @ViewChild('videoElement') videoElement: any;
  video: any;

  videoStream: MediaStream;
  videoBlob;
  videoBlobUrl;
  videoName;
  preVideoCountdown = false;
  startSeconds = 3;
  isVideoRecording = false;
  videoMuted = true;
  videoCantStart = false;
  maxRecordTimeInSeconds = 60;
  formattedMaxRecordTimeInSeconds = '01:00';
  videoRecordedTime = '00:00';
  videoDuration: Duration;
  videoConf = {
    video: {
      facingMode: 'user',
      height: { ideal: 720 },
      width: { ideal: 1280 }
    },
    audio: true
  };
  preRecordingId;
  recordingId;

  constructor(
    private ref: ChangeDetectorRef,
    private videoRecordingService: VideoRecordingService,
    private sanitizer: DomSanitizer) {

    this.videoRecordingService.recordingFailed().subscribe(() => {
      this.isVideoRecording = false;
      this.videoCantStart = true;
      this.ref.detectChanges();
    });

    this.videoRecordingService.getRecordedTime().subscribe((time) => {
      this.videoDuration = time;
      this.videoRecordedTime = this.toString(time.minutes()) + ':' + this.toString(time.seconds());
      this.ref.detectChanges();
    });

    this.videoRecordingService.getStream().subscribe((stream) => {
      this.videoStream = stream;
      this.videoCantStart = false;
      this.ref.detectChanges();
    });

    this.videoRecordingService.getRecordedBlob().subscribe((data) => {
      this.videoBlob = data;
      this.videoName = data.title;
      this.videoBlobUrl = this.sanitizer.bypassSecurityTrustUrl(data.url);
      this.videoCantStart = false;
      this.ref.detectChanges();
    });
  }

  ngOnInit(): void {
    this.videoCantStart = false;

    setTimeout(() => {
      this.video = this.videoElement.nativeElement;
    }, 0);
  }

  ngOnDestroy(): void {
  }

  hideModal() {
    this.videoRecorderModal.hide();
    this.stopVideoRecording();
    this.clearVideoRecordedData();
  }

  showModal() {
    this.clearVideoRecordedData();
    this.videoRecorderModal.show();
    this.showCamera();
  }

  completeVideo() {
    if (this.videoBlob) {
      const model = new VideoModel();
      model.duration = this.videoDuration;
      model.videoBlob = this.videoBlob;
      this.videoCompleted.emit(model);
    }

    this.hideModal();
  }

  showCamera() {
    this.videoCantStart = false;
    this.videoMuted = true;
    this.videoRecordingService.showCamera(this.videoConf).then(stream => {
      this.video.srcObject = stream;
      this.video.play();
    })
    .catch(function (err) {
      console.log(err.name + ': ' + err.message);
    });
  }

  startVideoRecording() {
    if (this.isVideoRecording) {
      return;
    }

    this.videoCantStart = false;
    this.videoMuted = true;
    this.startSeconds = 3;
    this.startCountdown();
  }

  startCountdown() {
    this.preVideoCountdown = true;

    const context = this;
    this.preRecordingId = setTimeout(() => {
      context.startSeconds = context.startSeconds - 1;
      context.ref.detectChanges();

      if (context.startSeconds <= 0) {
        context.preVideoCountdown = false;
        context.video.controls = false;
        context.isVideoRecording = true;

        context.videoRecordingService.startRecording(context.videoConf)
          .then(stream => {
            context.video.srcObject = stream;
            context.video.play();

            context.recordingId = setTimeout(() => {
              if (context.isVideoRecording) {
                context.stopVideoRecording();
              }
            }, (context.maxRecordTimeInSeconds * 1000));
          })
          .catch(function (err) {
            console.log(err.name + ': ' + err.message);
          });
      } else {
        context.startCountdown();
      }
    }, 1000);
  }

  stopVideoRecording() {
    if (this.isVideoRecording) {
      this.videoRecordingService.stopRecording();
      this.video.srcObject = this.videoBlobUrl;
      this.video.play();
      this.isVideoRecording = false;
      this.preVideoCountdown = false;
      this.videoMuted = false;
      this.startSeconds = 3;
      this.video.controls = true;
      if (this.recordingId) {
        clearTimeout(this.recordingId);
      }
    }

    if (this.preVideoCountdown) {
      this.preVideoCountdown = false;
      this.startSeconds = 3;
      if (this.preRecordingId) {
        clearTimeout(this.preRecordingId);
      }
    }
  }

  clearVideoRecordedData(showCamera = false) {
    this.videoBlobUrl = null;
    this.video.srcObject = null;
    this.video.controls = false;
    this.videoRecordedTime = '00:00';
    this.isVideoRecording = false;
    this.preVideoCountdown = false;
    this.videoMuted = true;
    this.startSeconds = 3;

    this.videoRecordingService.stopRecording();
    if (showCamera) {
      this.showCamera();
    }
    if (this.recordingId) {
      clearTimeout(this.recordingId);
    }
    this.ref.detectChanges();
  }

  private toString(value) {
    let val = value;
    if (!value) {
      val = '00';
    }
    if (value < 10) {
      val = '0' + value;
    }
    return val;
  }
}
